import { ArrowDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import ChatBubble from './ChatBubble';
import { whatsappTheme } from './whatsappTheme';

const { colors } = whatsappTheme;

const dayKey = (date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

const dayLabel = (date, today = new Date()) => {
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  if (dayKey(date) === dayKey(today)) return 'Today';
  if (dayKey(date) === dayKey(yesterday)) return 'Yesterday';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

const timeLabel = (date) => date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

/** Messages become rows: a day separator whenever the date changes, tail on the first bubble of each run. */
const buildRows = (messages) => {
  const rows = [];
  let previous = null;
  messages.forEach((message) => {
    const date = new Date(message.at);
    const valid = !Number.isNaN(date.getTime());
    const newDay = valid && (!previous || !previous.valid || dayKey(previous.date) !== dayKey(date));
    if (newDay) rows.push({ kind: 'day', key: `day-${message.id}`, label: dayLabel(date) });
    const startsRun = newDay || !previous || previous.direction !== message.direction;
    rows.push({
      kind: 'message',
      key: message.id,
      message,
      tail: startsRun,
      gap: startsRun && rows.length > 0,
      valid,
      date,
    });
    previous = { direction: message.direction, date, valid };
  });
  return rows;
};

/**
 * A WhatsApp-style conversation: day separators, runs of bubbles, wallpaper background, scrolled to
 * the newest message. Message text is rendered as plain text.
 * @param {{messages?: {id: string, direction: 'in' | 'out', text: string, at: string, status?: string | null, error?: string | null, message_type?: string, media_pending?: boolean, media_url?: string | null, media_mime_type?: string | null, media_filename?: string | null, reactions?: {in?: string, out?: string} | null, quote?: {id: string | null, sender: string | null, text: string} | null, editable?: boolean, edit_text?: string | null, deleted?: boolean}[], emptyText?: string, label?: string, className?: string, onFetchMedia?: (messageId: string) => Promise<{url: string, mime_type: string}>, onReply?: (message: any) => void, onReact?: (messageId: string, emoji: string) => void, onEdit?: (message: any) => void, onDelete?: (messageId: string) => void}} props
 */
const ChatThread = ({
  messages = [],
  emptyText = 'No messages yet',
  label = 'Conversation',
  className = '',
  onFetchMedia,
  onReply,
  onReact,
  onEdit,
  onDelete,
}) => {
  const regionRef = useRef(null);
  // True while the reader sits at (or near) the bottom; only scroll events change it.
  const nearBottomRef = useRef(true);
  const prevNewestRef = useRef(null);
  const [showPill, setShowPill] = useState(false);

  const list = messages || [];
  const newestId = list.length > 0 ? list[list.length - 1].id : null;
  const newestDirection = list.length > 0 ? list[list.length - 1].direction : null;

  // Jump to the quoted message and flash it briefly, like WhatsApp. The quoted row is always on the
  // page (the backend only sends a quote id for messages it found on it).
  const scrollToMessage = (id) => {
    const target = regionRef.current?.querySelector(`[data-message-id="${id}"]`);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add('bg-black/10');
    setTimeout(() => target.classList.remove('bg-black/10'), 1200);
  };

  const scrollToBottom = () => {
    const region = regionRef.current;
    if (region) region.scrollTop = region.scrollHeight;
  };

  // Follow the conversation when a NEW message lands. Already near the bottom (or it is our own
  // message): jump. Scrolled up reading history and a customer message arrives: leave the scroll alone
  // and offer a "New messages" pill instead.
  useEffect(() => {
    const first = prevNewestRef.current === null;
    const changed = prevNewestRef.current !== newestId;
    prevNewestRef.current = newestId;
    if (!changed && !first) return;
    if (first || nearBottomRef.current || newestDirection === 'out') {
      scrollToBottom();
      setShowPill(false);
    } else if (newestDirection === 'in') {
      setShowPill(true);
    }
  }, [list.length, newestId, newestDirection]);

  const onScroll = () => {
    const region = regionRef.current;
    if (!region) return;
    const near = region.scrollHeight - region.scrollTop - region.clientHeight < 80;
    nearBottomRef.current = near;
    if (near) setShowPill(false);
  };

  return (
    <div className={`relative flex min-h-0 flex-col ${className}`}>
      <div
        ref={regionRef}
        onScroll={onScroll}
        role="region"
        aria-label={label}
        className="min-h-0 flex-1 overflow-y-auto px-3 py-3"
        style={{ backgroundColor: colors.wallpaper, fontFamily: whatsappTheme.fontFamily }}
      >
        {list.length === 0 ? (
          <p className="py-6 text-center text-[13px]" style={{ color: colors.textSecondary }}>
            {emptyText}
          </p>
        ) : (
          buildRows(list).map((row) =>
            row.kind === 'day' ? (
              <div key={row.key} className="my-2 flex justify-center">
                <span
                  data-testid="day-separator"
                  className="rounded-[7.5px] px-3 py-[5px] text-[12.5px] shadow-[0_1px_0.5px_rgba(11,20,26,0.13)]"
                  style={{ backgroundColor: colors.separatorPill, color: colors.textSecondary }}
                >
                  {row.label}
                </span>
              </div>
            ) : (
              <div
                key={row.key}
                data-testid="message-row"
                data-message-id={row.message.id}
                className={`rounded-md transition-colors duration-500 ${row.gap ? 'mt-2' : 'mt-[2px]'}`}
              >
                <ChatBubble
                  direction={row.message.direction}
                  tail={row.tail}
                  time={row.valid ? timeLabel(row.date) : ''}
                  status={row.message.status}
                  media={
                    row.message.media_url || row.message.media_pending
                      ? {
                          url: row.message.media_url,
                          mimeType: row.message.media_mime_type,
                          filename: row.message.media_filename,
                          pending: !!row.message.media_pending,
                          messageType: row.message.message_type,
                          onFetch: onFetchMedia ? () => onFetchMedia(row.message.id) : undefined,
                        }
                      : null
                  }
                  reactions={row.message.reactions}
                  quote={row.message.quote}
                  linkPreview={row.message.link_preview}
                  onQuoteClick={() => scrollToMessage(row.message.quote?.id)}
                  deleted={!!row.message.deleted}
                  below={
                    row.message.status === 'failed' && row.message.error ? (
                      <p className="mt-[2px] max-w-full text-[11px] leading-snug text-red-600" data-testid="send-error">
                        {row.message.error}
                      </p>
                    ) : null
                  }
                  onReply={onReply ? () => onReply(row.message) : undefined}
                  onReact={onReact ? (emoji) => onReact(row.message.id, emoji) : undefined}
                  onEdit={onEdit && row.message.direction === 'out' && row.message.editable ? () => onEdit(row.message) : undefined}
                  onDelete={onDelete ? () => onDelete(row.message.id) : undefined}
                >
                  {row.message.text}
                </ChatBubble>
              </div>
            ),
          )
        )}
      </div>
      {showPill && (
        <button
          type="button"
          onClick={() => {
            scrollToBottom();
            nearBottomRef.current = true;
            setShowPill(false);
          }}
          className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-[#008069] px-3 py-1 text-xs font-medium text-white shadow-lg"
        >
          <ArrowDown size={14} />
          New messages
        </button>
      )}
    </div>
  );
};

export default ChatThread;
